// Closed captions on a native media element. A `<video>` carries its caption
// and subtitle tracks in `textTracks`; showing one is a matter of its `mode`.
// The chrome's CC toggle reads and writes that mode here, and the playback
// coordinator persists the choice per track (`captions` in the playback
// state) so a runtime that remounts — the dock taking a video over for
// cinema, the post's own player getting it back — shows the same captions.

type TextTrackLike = {
    kind?: string
    language?: string
    mode?: string
}

type TextTrackListLike = ArrayLike<TextTrackLike>

export type MediaElementCaptionsLike = {
    textTracks?: TextTrackListLike | null
}

export type CaptionsState = {
    available: boolean
    enabled: boolean
}

export const CAPTIONS_OFF: CaptionsState = { available: false, enabled: false }

export function isCaptionTextTrack(track: TextTrackLike | null | undefined): boolean {
    return track?.kind === 'captions' || track?.kind === 'subtitles'
}

function listCaptionTracks(mediaEl: MediaElementCaptionsLike | null | undefined): TextTrackLike[] {
    const tracks = mediaEl?.textTracks
    if (!tracks) return []
    const captionTracks: TextTrackLike[] = []
    for (let index = 0; index < tracks.length; index += 1) {
        const track = tracks[index]
        if (isCaptionTextTrack(track)) captionTracks.push(track)
    }
    return captionTracks
}

export function readMediaElementCaptionsState(
    mediaEl: MediaElementCaptionsLike | null | undefined,
): CaptionsState {
    const tracks = listCaptionTracks(mediaEl)
    if (!tracks.length) return CAPTIONS_OFF
    return {
        available: true,
        enabled: tracks.some(track => track.mode === 'showing'),
    }
}

function languageBase(language: string | null | undefined): string {
    return String(language || '')
        .toLowerCase()
        .split('-')[0]
}

/**
 * The track a CC toggle should show: the one already showing, else the first
 * in the viewer's language (a `captions` track outranks `subtitles`, which
 * carry no sound cues), else the first there is.
 */
export function pickPreferredCaptionTrack<T extends TextTrackLike>(
    tracks: T[],
    language: string | null | undefined = typeof navigator !== 'undefined'
        ? navigator.language
        : null,
): T | null {
    const candidates = tracks.filter(isCaptionTextTrack)
    if (!candidates.length) return null

    const showing = candidates.find(track => track.mode === 'showing')
    if (showing) return showing

    const base = languageBase(language)
    if (base) {
        const inLanguage = candidates.filter(track => languageBase(track.language) === base)
        const captions = inLanguage.find(track => track.kind === 'captions')
        if (captions) return captions
        if (inLanguage[0]) return inLanguage[0]
    }

    return candidates.find(track => track.kind === 'captions') || candidates[0]
}

/**
 * Shows the preferred caption track (hiding every other one) or hides them
 * all. Returns whether any track's mode changed.
 */
export function applyMediaElementCaptions(
    mediaEl: MediaElementCaptionsLike | null | undefined,
    enabled: boolean,
): boolean {
    const tracks = listCaptionTracks(mediaEl)
    if (!tracks.length) return false

    const preferred = enabled ? pickPreferredCaptionTrack(tracks) : null
    let changed = false
    for (const track of tracks) {
        const nextMode = track === preferred ? 'showing' : 'disabled'
        if (track.mode === nextMode) continue
        try {
            track.mode = nextMode
            changed = true
        } catch {
            // A track that refuses its mode stays as it is.
        }
    }
    return changed
}
